import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { Select, DatePicker } from 'components/ui';

import * as Styled from './DateRange.styles';

const today = dayjs().utc(true);
const periods = [
  {
    key: 'last30days',
    label: 'ostatnie 30 dni',
    fromDate: today.subtract(30, 'day').format('YYYY-MM-DD'),
    toDate: today.format('YYYY-MM-DD'),
  },
  {
    key: 'thisMonth',
    label: 'aktualny miesiąc',
    fromDate: today.startOf('month').format('YYYY-MM-DD'),
    toDate: today.endOf('month').format('YYYY-MM-DD'),
  },
  {
    key: 'lastMonth',
    label: 'poprzedni miesiąc',
    fromDate: today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    toDate: today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
  },
  {
    key: '12months',
    label: '12 miesięcy',
    fromDate: today.subtract(12, 'month').format('YYYY-MM-DD'),
    toDate: today.format('YYYY-MM-DD'),
  },
  {
    key: 'thisYear',
    label: 'aktualny rok',
    fromDate: today.startOf('year').format('YYYY-MM-DD'),
    toDate: today.endOf('year').format('YYYY-MM-DD'),
  },
  {
    key: 'lastYear',
    label: 'poprzedni rok',
    fromDate: today.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
    toDate: today.subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
  },
  {
    key: 'thisQuarter',
    label: 'aktualny kwartał',
    fromDate: today.startOf('quarter').format('YYYY-MM-DD'),
    toDate: today.endOf('quarter').format('YYYY-MM-DD'),
  },
  {
    key: 'lastQuarter',
    label: 'poprzedni kwartał',
    fromDate: today.subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD'),
    toDate: today.subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD'),
  },
  { key: 'custom', label: 'dowolny' },
];

export const DateRange = ({ onChange, fromDate, toDate }) => {
  const [currentDates, setCurrentDates] = useState({
    fromDate: fromDate || periods[0].fromDate,
    toDate: toDate || periods[0].toDate,
  });

  const currentPeriod = useMemo(
    () =>
      periods.find((item) => item.fromDate === currentDates.fromDate && item.toDate === currentDates.toDate) ||
      periods.find((item) => item.key === 'custom'),
    [currentDates],
  );

  const handleChangePeriod = (value) => {
    const period = periods.find((item) => item.key === value);

    setCurrentDates({ fromDate: period.fromDate, toDate: period.toDate });
  };

  useEffect(() => {
    if (fromDate !== currentDates.fromDate || toDate !== currentDates.toDate) {
      onChange && onChange(currentDates);
    }
  }, [currentDates, onChange, fromDate, toDate]);

  useEffect(() => {
    fromDate && toDate && setCurrentDates({ fromDate, toDate });
  }, [fromDate, toDate]);

  return (
    <Styled.Wrapper className="dateRangeWrapper">
      <Styled.SelectWrapper className="selectWrapper">
        <Select
          label="Okres*"
          value={currentPeriod.key}
          options={periods.map((period) => ({ value: period.key, label: period.label }))}
          onChange={handleChangePeriod}
        />
      </Styled.SelectWrapper>
      <Styled.DatesWrapper className="datesWrapper">
        <DatePicker
          label="Od*"
          value={currentDates.fromDate}
          maxDate={currentDates.toDate}
          onChange={(date) => setCurrentDates((prevState) => ({ ...prevState, fromDate: date }))}
        />
        <DatePicker
          label="Do*"
          value={currentDates.toDate}
          onChange={(date) => setCurrentDates((prevState) => ({ ...prevState, toDate: date }))}
        />
      </Styled.DatesWrapper>
    </Styled.Wrapper>
  );
};

DateRange.propTypes = {
  onChange: PropTypes.func,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
};
